import React from 'react';
import styles from 'v2/common/DRM/DataProduct/Modals/editModal.module.scss';
import classnames from 'classnames';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { FormControl } from 'react-bootstrap';
import restFactory from 'v2/utils/restFactory';
import { DataProduct, User } from 'v2/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import OwnersSelect from 'v2/form/CustomSelectors/OwnersSelect';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface EditOwnerModalProps {
  show: boolean;
  dataProduct: DataProduct;
  owner?: User;
  onClose: () => void;
}

export type DataSetOwnerForm = Pick<User, 'id' | 'job_title' | 'email'>;

const EditOwnerModal = ({
  show,
  dataProduct,
  owner,
  onClose,
}: EditOwnerModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    formState: { isValid },
    register,
    reset,
  } = useForm<DataSetOwnerForm>({
    mode: 'onChange',
    defaultValues: { id: dataProduct.owner, ...owner },
  });

  const {
    isLoading: isDataProductOwnerMutateLoading,
    mutate: dataProductOwnerMutate,
  } = useMutation(
    async (data: DataSetOwnerForm) => {
      return await restFactory.patch<DataProduct>(
        `/dj/api/v1/data_product/${dataProduct?.id}/`,
        { owner: data.id }
      );
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([
          'Get specific data product by ID',
          data.id,
        ]);
        queryClient.invalidateQueries(['DataVendorDetails_DataProductList']);

        toast.success(
          `${t(TranslationKeys.dataProduct_one, {
            postProcess: PostProcessKeys.TitleCase,
          })} owner saved.`
        );
        onClose();
      },
      onError: () => {
        toast.error(
          `Error saving ${t(TranslationKeys.dataProduct_one)} owner.`
        );
      },
    }
  );

  const onSubmit = async (data: DataSetOwnerForm) => {
    dataProductOwnerMutate(data);
  };

  function handleChange(option: User | undefined) {
    reset(option);
  }

  return (
    <ConfirmationModal
      className={styles.viewModals}
      centered={true}
      animation={true}
      show={show}
      onCancel={() => onClose()}
      okBtnText="Save"
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      isLoading={isDataProductOwnerMutateLoading}
      title={`Edit ${t(TranslationKeys.dataProduct_one, {
        postProcess: PostProcessKeys.TitleCase,
      })} Owner`}
    >
      <div className={classnames(styles.dataProductForm, 'mt-2')}>
        <span className={classnames(styles.titleDescription, 'mb-3')}>
          Designate an owner for this {t(TranslationKeys.dataProduct_one)}
        </span>
        <FormLabel label={`Owner name`} required={false} />
        <OwnersSelect owner={owner} onChange={handleChange} />
        <FormLabel label={`Title`} required={false} className="mt-3" />
        <FormControl
          type="text"
          autoComplete="off"
          readOnly={true}
          {...register('job_title', { required: false })}
        />
        <FormLabel label={`Email Address`} required={false} className="mt-3" />
        <FormControl
          type="text"
          autoComplete="off"
          readOnly={true}
          {...register('email', { required: false })}
        />
        <FormControl
          type="hidden"
          readOnly={true}
          {...register('id', {
            required: true,
            validate: (value) => owner?.id !== value,
          })}
        />
      </div>
    </ConfirmationModal>
  );
};

export default EditOwnerModal;
